/**
 * Workflow Status Engine — Jobs
 * Defines the full lifecycle of a photography job workflow.
 */

export type WorkflowStatus =
  | "scheduled"
  | "confirmed"
  | "shooting"
  | "editing"
  | "waiting_client"
  | "rendering"
  | "delivered"
  | "completed"
  | "cancelled";

export type WorkflowStatusConfig = {
  value: WorkflowStatus;
  label: string;
  shortLabel: string;
  color: string;
  bg: string;
  border: string;
  icon: string;
  description: string;
  order: number;
};

export const WORKFLOW_STATUSES: WorkflowStatusConfig[] = [
  {
    value: "scheduled",
    label: "Scheduled",
    shortLabel: "Scheduled",
    color: "text-sky-600",
    bg: "bg-sky-50",
    border: "border-sky-200",
    icon: "ri-calendar-event-line",
    description: "Shoot date is booked and confirmed",
    order: 0,
  },
  {
    value: "confirmed",
    label: "Confirmed",
    shortLabel: "Confirmed",
    color: "text-indigo-600",
    bg: "bg-indigo-50",
    border: "border-indigo-200",
    icon: "ri-checkbox-circle-line",
    description: "Client confirmed, all details locked",
    order: 1,
  },
  {
    value: "shooting",
    label: "Shooting",
    shortLabel: "Shooting",
    color: "text-amber-600",
    bg: "bg-amber-50",
    border: "border-amber-200",
    icon: "ri-camera-3-line",
    description: "Shoot day — session in progress",
    order: 2,
  },
  {
    value: "editing",
    label: "Editing",
    shortLabel: "Editing",
    color: "text-violet-600",
    bg: "bg-violet-50",
    border: "border-violet-200",
    icon: "ri-edit-2-line",
    description: "Post-processing and editing in progress",
    order: 3,
  },
  {
    value: "waiting_client",
    label: "Waiting Client",
    shortLabel: "Waiting",
    color: "text-orange-600",
    bg: "bg-orange-50",
    border: "border-orange-200",
    icon: "ri-user-voice-line",
    description: "Waiting for client photo selection or approval",
    order: 4,
  },
  {
    value: "rendering",
    label: "Rendering",
    shortLabel: "Rendering",
    color: "text-teal-600",
    bg: "bg-teal-50",
    border: "border-teal-200",
    icon: "ri-loader-3-line",
    description: "Final export and rendering in progress",
    order: 5,
  },
  {
    value: "delivered",
    label: "Delivered",
    shortLabel: "Delivered",
    color: "text-emerald-600",
    bg: "bg-emerald-50",
    border: "border-emerald-200",
    icon: "ri-send-plane-fill",
    description: "Files delivered to client",
    order: 6,
  },
  {
    value: "completed",
    label: "Completed",
    shortLabel: "Done",
    color: "text-emerald-700",
    bg: "bg-emerald-100",
    border: "border-emerald-300",
    icon: "ri-checkbox-circle-fill",
    description: "Job fully completed and archived",
    order: 7,
  },
  {
    value: "cancelled",
    label: "Cancelled",
    shortLabel: "Cancelled",
    color: "text-red-500",
    bg: "bg-red-50",
    border: "border-red-200",
    icon: "ri-close-circle-line",
    description: "Job cancelled",
    order: 8,
  },
];

export const WORKFLOW_STATUS_MAP = new Map<WorkflowStatus, WorkflowStatusConfig>(
  WORKFLOW_STATUSES.map((s) => [s.value, s])
);

export function getWorkflowStatus(value: string): WorkflowStatusConfig {
  return WORKFLOW_STATUS_MAP.get(value as WorkflowStatus) ?? WORKFLOW_STATUSES[0];
}

/**
 * Returns which timestamp columns should be set when transitioning to a new status.
 */
export function getTimestampsForStatus(newStatus: WorkflowStatus, now: string): Record<string, string | null> {
  const timestamps: Record<string, string | null> = {};
  if (newStatus === "editing") {
    timestamps.editing_started_at = now;
  }
  if (newStatus === "completed") {
    timestamps.completed_at = now;
  }
  if (newStatus === "delivered") {
    timestamps.delivered_at = now;
  }
  // Clear timestamps when going back
  if (newStatus === "scheduled" || newStatus === "confirmed" || newStatus === "shooting") {
    timestamps.editing_started_at = null;
    timestamps.completed_at = null;
  }
  return timestamps;
}

/**
 * Portal-friendly label for a workflow status.
 */
export function getPortalLabel(status: string): string {
  const portalLabels: Record<string, string> = {
    scheduled: "Your session is scheduled",
    confirmed: "Session confirmed",
    shooting: "Session in progress",
    editing: "Photos being edited",
    waiting_client: "Awaiting your selection",
    rendering: "Preparing your files",
    delivered: "Files are ready",
    completed: "Project complete",
    cancelled: "Session cancelled",
    // legacy fallbacks
    in_progress: "Session in progress",
    waiting: "Awaiting your input",
  };
  return portalLabels[status] ?? status.replace(/_/g, " ");
}

/**
 * Ordered pipeline steps for the progress bar.
 * Does not include cancelled (it's a terminal off-track state).
 */
export const PIPELINE_STEPS: WorkflowStatus[] = [
  "scheduled",
  "confirmed",
  "shooting",
  "editing",
  "waiting_client",
  "rendering",
  "delivered",
  "completed",
];

export function getPipelineIndex(status: string): number {
  return PIPELINE_STEPS.indexOf(status as WorkflowStatus);
}